import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { QualityCheckRow } from '@/utils/analyticsCompute';

export interface QualityScorecardTableProps {
  checks: QualityCheckRow[];
  loading?: boolean;
}

const statusBadgeVariant: Record<string, 'pass' | 'warn' | 'fail'> = {
  pass: 'pass',
  warn: 'warn',
  fail: 'fail',
};

const STATUS_LABELS: Record<string, string> = {
  pass: 'Pass',
  warn: 'Warn',
  fail: 'Fail',
};

export const QualityScorecardTable: React.FC<QualityScorecardTableProps> = ({
  checks,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (checks.length === 0) {
    return (
      <div className="p-3">
        <p className="text-xs text-muted-foreground">No quality checks available.</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Check</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Value</th>
              <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">Threshold</th>
              <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Status</th>
              <th className="w-16 py-1.5 px-2 text-muted-foreground font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((row) => (
              <tr key={row.id} className="border-b border-border/40 hover:bg-muted/20">
                <td className="py-1.5 px-2 font-medium text-foreground">{row.label}</td>
                <td className="py-1.5 px-2 tabular-nums text-foreground text-right">{row.value}</td>
                <td className="py-1.5 px-2 tabular-nums text-muted-foreground text-right">{row.threshold}</td>
                <td className="py-1.5 px-2">
                  <Badge variant={statusBadgeVariant[row.status] ?? 'outline'} size="sm">{STATUS_LABELS[row.status] ?? row.status}</Badge>
                </td>
                <td className="py-1.5 px-2 text-right">
                  {row.viewOffendersHref ? (
                    <Link
                      to={row.viewOffendersHref}
                      className="inline-flex items-center justify-end gap-0.5 text-primary text-[11px] font-medium hover:underline"
                      title="View offenders"
                    >
                      View
                      <ChevronRight className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="text-muted-foreground text-[10px]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
